import React, { useState } from "react";
import { LogoPulse } from "../Loader";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DatabaseBackup, ShieldCheck, History, Download, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
const countsLine = (c = {}) => Object.entries(c).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ");

function UserRow({ row }) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const { data: versions = [], isFetching } = useQuery({
        queryKey: ["user-backup-versions", row.user_id],
        queryFn: () => api.get(`/admin/user-backups/${row.user_id}`).then((r) => r.data),
        enabled: open,
    });
    const refresh = () => { qc.invalidateQueries({ queryKey: ["user-backup-status"] }); qc.invalidateQueries({ queryKey: ["user-backup-versions", row.user_id] }); };

    const backupNow = async () => { setBusy(true); try { const r = await api.post(`/admin/user-backups/${row.user_id}`); toast.success(r.data?.skipped ? "Already up to date — no changes" : "Backup created"); refresh(); } catch { toast.error("Backup failed"); } finally { setBusy(false); } };
    const download = async (bid, when) => {
        setBusy(true);
        try {
            const r = await api.get(`/admin/user-backups/${row.user_id}/${bid}/download`);
            const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `backup-${(row.email || row.user_id).replace(/[^a-z0-9]/gi, "_")}-${new Date(when).toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
        } catch { toast.error("Download failed"); } finally { setBusy(false); }
    };

    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.03]" data-testid={`backup-user-${row.user_id}`}>
            <div className="flex items-center gap-3 px-3 py-2.5">
                <button onClick={() => setOpen((v) => !v)} className="text-white/50 hover:text-white" data-testid={`backup-expand-${row.user_id}`}>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white truncate">{row.name || row.email} {row.role !== "user" && <span className="text-[9px] uppercase tracking-wider text-[#FCD34D] ml-1">{row.role}</span>}</p>
                    <p className="text-[11px] text-white/45 truncate">{row.email}</p>
                </div>
                <div className="text-right hidden sm:block">
                    <p className="text-[11px] text-white/70" data-testid={`backup-latest-${row.user_id}`}>{row.versions > 0 ? fmt(row.latest) : "No backup yet"}</p>
                    <p className="text-[10px] text-white/40">{row.versions} version{row.versions !== 1 ? "s" : ""}</p>
                </div>
                <button onClick={backupNow} disabled={busy} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#9D4CDD]/20 text-[#C9A6F0] border border-[#9D4CDD]/40 hover:bg-[#9D4CDD]/30 disabled:opacity-40" data-testid={`backup-now-${row.user_id}`}>{busy ? <LogoPulse className="h-3.5 w-3.5" /> : <DatabaseBackup className="h-3.5 w-3.5" />} Backup</button>
            </div>
            {open && (
                <div className="border-t border-white/10 px-3 py-2 space-y-1.5" data-testid={`backup-versions-${row.user_id}`}>
                    {isFetching ? <p className="text-xs text-white/40 py-2">Loading versions…</p> : !versions.length ? <p className="text-xs text-white/40 py-2">No backups stored yet — they are created automatically every ~30 min when data changes.</p> : versions.map((v) => (
                        <div key={v.id} className="flex items-center gap-3 rounded-lg bg-black/20 px-3 py-2" data-testid={`backup-version-${v.id}`}>
                            <History className="h-3.5 w-3.5 text-white/40 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-white/80">{fmt(v.created_at)} <span className="text-[9px] uppercase tracking-wider text-white/40 ml-1">{v.reason}</span></p>
                                <p className="text-[10px] text-white/40 truncate">{countsLine(v.counts)}</p>
                            </div>
                            <button onClick={() => download(v.id, v.created_at)} disabled={busy} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#9D4CDD]/15 text-[#C9A6F0] border border-[#9D4CDD]/40 hover:bg-[#9D4CDD]/25 disabled:opacity-40" data-testid={`backup-download-${v.id}`}><Download className="h-3.5 w-3.5" /> Download</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function UserBackupsAdmin() {
    const { isOwner } = useAuth();
    const { data: rows = [], isLoading } = useQuery({ queryKey: ["user-backup-status"], queryFn: () => api.get("/admin/user-backups").then((r) => r.data), enabled: isOwner });

    if (!isOwner) return null;
    const withBackup = rows.filter((r) => r.versions > 0).length;

    return (
        <div className="space-y-4" data-testid="user-backups-admin">
            <div className="rounded-2xl border border-[#22C55E]/25 bg-gradient-to-br from-[#0c1a10] to-[#0A0B10] p-4 sm:p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#86EFAC] flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Disaster Recovery</p>
                <h3 className="font-heading text-xl sm:text-2xl font-black uppercase tracking-tight text-white mt-1">Automatic User Backups</h3>
                <p className="text-xs text-white/60 max-w-2xl mt-1">Every account is snapshotted automatically in the background (~every 30 min, only when data changes) — collection &amp; pucks, opened packs, predictions, favourites, preferences and account settings. Up to 10 versions are kept per user. Owner-only; invisible to normal users.</p>
                <p className="text-[11px] text-white/45 max-w-2xl mt-1.5">Download any snapshot as a JSON file to keep a safe copy off-platform.</p>
                <div className="flex flex-wrap gap-4 mt-3 text-xs">
                    <span className="text-white/70"><b className="text-white" data-testid="backup-total-users">{rows.length}</b> accounts</span>
                    <span className="text-white/70"><b className="text-[#86EFAC]">{withBackup}</b> with a backup</span>
                    <span className="text-white/70"><b className="text-[#FCD34D]">{rows.length - withBackup}</b> awaiting first snapshot</span>
                </div>
            </div>
            {isLoading ? <p className="text-sm text-white/50">Loading backup status…</p> : (
                <div className="space-y-2">{rows.map((r) => <UserRow key={r.user_id} row={r} />)}</div>
            )}
        </div>
    );
}
